import { useIntl, type UserRequirement, useSetPageTitle, WEB_RECOVERY_TIME_BETWEEN_MODALS } from '@capsicum/common';
import { Button, TextInput, useInfoModal } from '@capsicum/common-web';
import { useUkWarning } from '@capsicum/features/regulations/shared';
import { useTrackUKRegulationEvents } from '@capsicum/socios-core';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { UkRiskWarningConfirmationBody } from './UkRiskWarningConfirmationBody';

export type UkRiskWarningProps = {
	isForcedRequirement?: boolean;
	onDismiss: (callback?: () => void) => void;
	requirement: UserRequirement;
};

export const UkRiskWarning = ({ isForcedRequirement, onDismiss, requirement }: UkRiskWarningProps) => {
	const { fullName, handleSubmitForm, isFullnameFilled, isSubmitLoading, submitRequirement, ukWarningForm } =
		useUkWarning({
			requirement
		});
	const { trackUKRiskWarningDecline } = useTrackUKRegulationEvents();
	const { $t } = useIntl();
	const { showInfoModal } = useInfoModal();

	const [showForm, setShowForm] = useState(false);

	useSetPageTitle($t('turkishExtraFields.headerTitle'), $t('jurisdictionRequirements.requirement'));

	useEffect(() => {
		if (!isFullnameFilled) {
			return showIntroductionModal();
		}

		showConfirmModal();
	}, [isFullnameFilled]);

	const showIntroductionModal = () => {
		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: () => setShowForm(true),
				primaryInvokeOnDismiss: false
			},
			buttonTexts: {
				primaryButtonText: $t('global.button.continue')
			},
			image: 'warning',
			isClosable: !isForcedRequirement,
			onDismiss,
			subtitle: $t('jurisdictionRequirements.ukRiskWarning.introduction'),
			testID: 'uk-risk-warning-introduction-modal',
			title: $t('jurisdictionRequirements.ukRiskWarning.introduction'),
			variant: 'alert'
		});
	};

	const showConfirmModal = () => {
		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: submitRequirement,
				onSecondaryButtonHandler: trackUKRiskWarningDecline,
				primaryInvokeOnDismiss: false
			},
			buttonTexts: {
				primaryButtonText: $t('global.button.proceed'),
				secondaryButtonText: $t('global.button.decline')
			},
			content: <UkRiskWarningConfirmationBody fullName={fullName} />,
			image: 'warning',
			isClosable: false,
			onDismiss: () => setTimeout(() => onDismiss(showConfirmModal), WEB_RECOVERY_TIME_BETWEEN_MODALS),
			subtitle: $t('jurisdictionRequirements.ukRiskWarning.description'),
			testID: 'uk-risk-warning-confirmation-modal',
			title: $t('jurisdictionRequirements.ukRiskWarning.riskWarning'),
			variant: 'confirm'
		});
	};

	const submitForm = () => {
		handleSubmitForm();
		setShowForm(false);
	};

	if (!showForm) return <S.EmptyContainer />;

	return (
		<S.Wrapper>
			<S.MainContentContainer>
				<FormProvider {...ukWarningForm}>
					<S.BlockContainer>
						<S.BlockTitle>{$t('jurisdictionRequirements.ukRiskWarning.form.title')}</S.BlockTitle>
						<S.InputsContainer>
							<TextInput
								label={$t('jurisdictionRequirements.ukRiskWarning.form.firstName.label')}
								name="firstName"
								placeholder={$t('jurisdictionRequirements.ukRiskWarning.form.firstName.placeholder')}
								testID="uk-risk-warning-first-name-input"
							/>
							<TextInput
								label={$t('jurisdictionRequirements.ukRiskWarning.form.lastName.label')}
								name="lastName"
								placeholder={$t('jurisdictionRequirements.ukRiskWarning.form.lastName.placeholder')}
								testID="uk-risk-warning-last-name-input"
							/>
							<S.ButtonsContainer>
								<Button
									disabled={!ukWarningForm.formState.isValid}
									fullWidth
									isLoading={isSubmitLoading}
									testID="uk-risk-warning-confirm-button"
									text={$t('global.button.confirm')}
									onClick={submitForm}
								/>
							</S.ButtonsContainer>
						</S.InputsContainer>
					</S.BlockContainer>
				</FormProvider>
			</S.MainContentContainer>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0 51px`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 51px`};
		}
	`;

	export const MainContentContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const BlockContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const BlockTitle = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
	`;

	export const InputsContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ theme }) => `${theme.spacings.large} ${theme.spacings.small}`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const ButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const EmptyContainer = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;
}
